import React, { useState, useContext, ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useWindowSize } from 'react-use';
import { DropdownItem, DropdownMenu } from '../../components/bootstrap/Dropdown';
import Button from '../../components/bootstrap/Button';
import useDarkMode from '../../hooks/useDarkMode';
import Collapse from '../../components/bootstrap/Collapse';
import { NavigationLine } from '../Navigation/Navigation';
import Icon from '../../components/icon/Icon';
import useNavigationItemHandle from '../../hooks/useNavigationItemHandle';
import AuthContext from '../../contexts/authContext';
import ThemeContext from '../../contexts/themeContext';
import { demoPagesMenu } from '../../menu';

// Avatar gradient palette — same logic as UserProfileDropdown
const AVATAR_GRADIENTS = [
	['#4f46e5', '#7c3aed'],
	['#0ea5e9', '#0284c7'],
	['#10b981', '#059669'],
	['#f59e0b', '#d97706'],
	['#ef4444', '#dc2626'],
	['#8b5cf6', '#7c3aed'],
	['#ec4899', '#db2777'],
	['#06b6d4', '#0891b2'],
];
const getAvatarGradient = (name: string): string => {
	let hash = 0;
	for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
	const [a, b] = AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
	return `linear-gradient(135deg, ${a} 0%, ${b} 100%)`;
};
const getInitials = (name: string, surname: string): string => {
	const first = (name || '').trim().charAt(0).toUpperCase();
	const last = (surname || '').trim().charAt(0).toUpperCase();
	return first && last ? `${first}${last}` : first || last || 'U';
};

const User = () => {
	const { width } = useWindowSize();
	const { setAsideStatus } = useContext(ThemeContext);
	const { userData, setUser } = useContext(AuthContext);

	const navigate = useNavigate();
	const handleItem = useNavigationItemHandle();
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();

	const [collapseStatus, setCollapseStatus] = useState<boolean>(false);

	const { t } = useTranslation(['translation', 'menu']);

	const displayName = useMemo(() => {
		if (userData?.name && userData?.surname) return `${userData.name} ${userData.surname}`;
		if (userData?.name) return userData.name;
		if (userData?.email) return userData.email;
		return 'User';
	}, [userData]);

	const initials = useMemo(
		() => getInitials(userData?.name || '', userData?.surname || ''),
		[userData],
	);

	const avatarGradient = useMemo(() => getAvatarGradient(displayName), [displayName]);

	return (
		<>
			<div
				className={classNames('user', { open: collapseStatus })}
				role='presentation'
				onClick={() => setCollapseStatus(!collapseStatus)}>
				<div className='user-avatar'>
					<span
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: '100%',
							height: '100%',
							background: avatarGradient,
							borderRadius: '50%',
							color: '#fff',
							fontWeight: 700,
							fontSize: '1rem',
							letterSpacing: '0.03em',
							userSelect: 'none',
						}}>
						{initials}
					</span>
				</div>
				<div className='user-info'>
					<div className='user-name d-flex align-items-center'>
						{displayName}
						<Icon icon='Verified' className='ms-1' color='info' />
					</div>
					<div className='user-sub-title'>{userData?.position || userData?.email || ''}</div>
				</div>
			</div>
			<DropdownMenu>
				<DropdownItem>
					<Button
						icon='AccountBox'
						onClick={() => {
							// Profile functionality temporarily disabled - appointment menu removed
							// navigate(`../${demoPagesMenu.appointment.subMenu.employeeID.path}/${userData?.id}`);
							console.log('Profile clicked - functionality to be implemented');
						}}>
						Profile
					</Button>
				</DropdownItem>
				<DropdownItem>
					<Button
						icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
						onClick={() => setDarkModeStatus(!darkModeStatus)}
						aria-label='Toggle fullscreen'>
						{darkModeStatus ? 'Dark Mode' : 'Light Mode'}
					</Button>
				</DropdownItem>
			</DropdownMenu>

			<Collapse isOpen={collapseStatus} className='user-menu'>
				<nav aria-label='aside-bottom-user-menu'>
					<div className='navigation'>
						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
								// Profile functionality temporarily disabled - appointment menu removed
								// navigate(`../${demoPagesMenu.appointment.subMenu.employeeID.path}/${userData?.id}`, handleItem());
								console.log('Profile clicked - functionality to be implemented');
								handleItem();
							}}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon icon='AccountBox' className='navigation-icon' />
									<span className='navigation-text'>
										{t('menu:Profile') as ReactNode}
									</span>
								</span>
							</span>
						</div>
						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
								setDarkModeStatus(!darkModeStatus);
								handleItem();
							}}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon
										icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
										color={darkModeStatus ? 'info' : 'warning'}
										className='navigation-icon'
									/>
									<span className='navigation-text'>
										{darkModeStatus
											? (t('menu:DarkMode') as ReactNode)
											: (t('menu:LightMode') as ReactNode)}
									</span>
								</span>
							</span>
						</div>
					</div>
				</nav>
				<NavigationLine />
				<nav aria-label='aside-bottom-user-menu-2'>
					<div className='navigation'>
						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
								if (setUser) {
									setUser('');
								}
								if (width < Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE)) {
									setAsideStatus(false);
								}
								navigate(`../${demoPagesMenu.logout.path}`);
							}}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon icon='Logout' className='navigation-icon' />
									<span className='navigation-text'>
										{t('menu:Logout') as ReactNode}
									</span>
								</span>
							</span>
						</div>
					</div>
				</nav>
			</Collapse>
		</>
	);
};

export default User;
